/**
 * Page combinée Détail/Modification d'un Exercice
 *
 * - Mode lecture par défaut
 * - Bouton Modifier → mode édition inline
 * - Création nouvel exercice si id === 'new' (lessonId requis en query param)
 */

import React from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import { useBookExerciseView } from './book-exercise-view/useBookExerciseView';
import { ExerciseViewHeader, ExerciseBackScreen } from './book-exercise-view/ExerciseViewHeader';
import { ExerciseEditForm } from './book-exercise-view/ExerciseEditForm';
import { ExerciseReadMain } from './book-exercise-view/ExerciseReadMain';
import { ExerciseReadSidebar } from './book-exercise-view/ExerciseReadSidebar';

/** Page Détail/Modification/Création d'un exercice de livre scolaire. */
const BookExerciseView: React.FC = () => {
  const v = useBookExerciseView();
  const { formData, error, lesson } = v;

  // Loading state
  if (v.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Vérifier lessonId pour création
  if (v.isNewMode && !v.lessonIdFromParams) {
    return <ExerciseBackScreen message="Le paramètre lessonId est requis pour créer un exercice" onBack={v.goToList} />;
  }

  // Error state
  if (!v.isNewMode && !v.exercise && !v.loading) {
    return <ExerciseBackScreen message={error || 'Exercice non trouvé'} onBack={v.goToList} />;
  }

  return (
    <Box p={3}>
      {/* Header */}
      <ExerciseViewHeader
        isNewMode={v.isNewMode}
        isEditMode={v.isEditMode}
        saving={v.saving}
        exerciseNumber={formData.exerciseNumber}
        title={formData.title}
        onBack={v.goToList}
        onEdit={v.handleEdit}
        onCancel={v.handleCancel}
        onSave={v.handleSave}
      />

      {/* Leçon parente */}
      {lesson && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Leçon : <strong>Leçon {lesson.lessonNumber}: {lesson.title}</strong>
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => v.setError('')}>
          {error}
        </Alert>
      )}

      {v.isEditMode ? (
        /* Mode Édition */
        <ExerciseEditForm formData={formData} handleChange={v.handleChange} tags={v.tags} />
      ) : (
        /* Mode Lecture */
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <ExerciseReadMain formData={formData} />
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <ExerciseReadSidebar formData={formData} />
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default BookExerciseView;
